/**
 * The tolerant reader for Marvin message parts, on a loaded conversation and on a run stream.
 *
 * The server rewrites every unit a client does not declare (`X-Marvin-Renders`) into a fallback,
 * but an App Store build is weeks behind the server. A new part kind, a new navigation kind, a
 * field a later contract added, or a part saved before the contract changed (an overlay card from
 * before `layers` with no revision pin) must cost that one part, never the message, the stream or
 * the conversation. So a part that does not parse is skipped with a console warning.
 *
 * What stays strict is the envelope around the parts: a message's own fields and a stream event's
 * `type`, `seq` and `data` (`conversationRuntime.ts`). An event type this build does not know is
 * not an envelope fault; the reducers pass over it.
 */
import { ApiStreamEventType } from "./streamProtocol";
import type { ApiStreamEvent } from "../api/run";
import type { ApiConversation, ApiMessage } from "../shared/interfaces";
import { parseApiMessagePart, type ApiMessagePart } from "./messageParts";

function warnSkipped(what: string, reason: string, value: unknown): void {
    console.warn(`Marvin: skipped ${what}: ${reason}`, value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isSeq(value: unknown): value is number {
    return Number.isSafeInteger(value) && Number(value) >= 0;
}

/** A saved message's readable parts in `seq` order. An unreadable part, or a second part with a
 *  `seq` already read, is skipped. A `parts` value that is not a list reads as no parts. */
export function readMessageParts(parts: unknown, messageId: string): ApiMessagePart[] {
    if (parts === undefined || parts === null) return [];
    if (!Array.isArray(parts)) {
        warnSkipped(`the parts of message ${messageId}`, "not a list", parts);
        return [];
    }
    const read: ApiMessagePart[] = [];
    const seen = new Set<number>();
    for (const value of parts) {
        const part = parseApiMessagePart(value);
        if (!part) {
            warnSkipped(`a part of message ${messageId}`, "it does not parse", value);
            continue;
        }
        if (seen.has(part.seq)) {
            warnSkipped(
                `a part of message ${messageId}`,
                `seq ${part.seq} was already read`,
                value,
            );
            continue;
        }
        seen.add(part.seq);
        read.push(part);
    }
    return read.sort((a, b) => a.seq - b.seq);
}

/** A loaded message with its parts read, and the answer it carries (`response`) too. A message
 *  with no `parts` keeps none: the renderer builds a legacy message's parts from its text. */
function readMessage(message: ApiMessage): ApiMessage {
    if (!isRecord(message)) return message;
    const read = { ...message };
    if (message.parts !== undefined && message.parts !== null) {
        read.parts = readMessageParts(message.parts, message.id);
    }
    if (isRecord(message.response)) read.response = readMessage(message.response);
    return read;
}

/**
 * A conversation as the API returned it, with every message's parts read once, here, so each
 * renderer of a loaded conversation (the web thread, the shared snapshot, the anchor chip, the
 * native runtime) sees only parts that parse. `conversation.ts` applies it to every read.
 */
export function readConversation(conversation: ApiConversation): ApiConversation {
    if (!isRecord(conversation) || !Array.isArray(conversation.messages)) return conversation;
    return { ...conversation, messages: conversation.messages.map(readMessage) };
}

/** Why a part event cannot be applied to the parts read so far, or null when it can. */
function unreadablePartEvent(
    parts: readonly ApiMessagePart[],
    event: ApiStreamEvent,
): string | null {
    const data: unknown = event.data;
    if (!isRecord(data)) return "its data is not an object";
    switch (event.type) {
        case ApiStreamEventType.PartCreated:
            return parseApiMessagePart(data.part) ? null : "the part does not parse";
        case ApiStreamEventType.PartCompleted:
            return isSeq(data.seq) ? null : "it names no part";
        case ApiStreamEventType.PartUpdated: {
            if (!isSeq(data.seq)) return "it names no part";
            const target = parts.find((part) => part.seq === data.seq);
            if (!target) return `part ${data.seq} was skipped or never arrived`;
            if (data.appendText !== undefined) {
                if (typeof data.appendText !== "string") return "the appended text is not text";
                return target.kind === "text" ? null : `part ${data.seq} is not text`;
            }
            if (data.payload === undefined) return "it changes nothing";
            return isRecord(data.payload) &&
                parseApiMessagePart({ ...target, payload: data.payload }) !== null
                ? null
                : "the replacement does not parse";
        }
        default:
            return null;
    }
}

/**
 * Whether a stream event's part content can be applied to the parts read so far. A part event
 * that cannot (a part that does not parse, an update to a part that was skipped, text appended to
 * a part that is not text, a replacement that does not parse) is skipped by the caller, never
 * fatal for the run. An event that is not a part event always applies.
 */
export function partEventApplies(parts: readonly ApiMessagePart[], event: ApiStreamEvent): boolean {
    const reason = unreadablePartEvent(parts, event);
    if (reason === null) return true;
    warnSkipped(`a ${event.type} event (seq ${event.seq})`, reason, event.data);
    return false;
}
